import { useCallback, useRef, useState } from "react";

/**
 * Single shared IntersectionObserver for all consumers.
 * One observer watching N elements is far cheaper than N separate observers.
 * Elements are automatically unobserved once they enter the viewport.
 */
const callbacks = new Map<Element, () => void>();
let sharedObserver: IntersectionObserver | null = null;

const ROOT_MARGIN = "1000px";

const getSharedObserver = () => {
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const cb = callbacks.get(entry.target);
            if (cb) {
              cb();
              callbacks.delete(entry.target);
              sharedObserver!.unobserve(entry.target);
            }
          }
        }
      },
      { rootMargin: ROOT_MARGIN }
    );
  }
  return sharedObserver;
};

/**
 * Returns a callback ref and a boolean indicating whether the element is (or has been)
 * within 1000px of the viewport. Once true, stays true — the element is never
 * re-hidden to avoid losing component state or causing layout shifts.
 *
 * Uses a callback ref rather than a RefObject so the observer re-attaches when
 * the underlying DOM node is swapped — e.g. a parent that briefly returns null
 * during a restore cycle and then re-mounts the wrapper.
 */
export const useNearViewport = (): [(el: HTMLDivElement | null) => void, boolean] => {
  const [isNear, setIsNear] = useState(false);
  const observedEl = useRef<HTMLDivElement | null>(null);

  const ref = useCallback((el: HTMLDivElement | null) => {
    if (observedEl.current === el) return;

    if (observedEl.current) {
      callbacks.delete(observedEl.current);
      sharedObserver?.unobserve(observedEl.current);
    }

    observedEl.current = el;

    if (el) {
      callbacks.set(el, () => setIsNear(true));
      getSharedObserver().observe(el);
    }
  }, []);

  return [ref, isNear];
};
